import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Code2, BookOpen, Copy, Check, Send, Loader2, ExternalLink, Mail, Plug } from 'lucide-react';
import { leadsAPI } from '@/lib/api';
import { INTEGRATIONS } from '@/lib/telemetry';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLES = {
  'pcf-reports': {
    title: 'List PCF reports',
    method: 'GET',
    path: '/api/pcf-reports',
    curl: `curl -X GET "${API_BASE}/pcf-reports" \\
  -H "Authorization: Bearer <YOUR_JWT_TOKEN>"`,
  },
  shipments: {
    title: 'Create an ICS2 shipment',
    method: 'POST',
    path: '/api/shipments',
    curl: `# First, fetch a sample payload
SAMPLE=$(curl -s "${API_BASE}/ics2/sample" -H "Authorization: Bearer <TOKEN>")

# Then POST it to create a shipment
curl -X POST "${API_BASE}/shipments" \\
  -H "Authorization: Bearer <TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d "$SAMPLE"`,
  },
  'ics2-xml': {
    title: 'Generate ICS2 ENS XML',
    method: 'POST',
    path: '/api/shipments/{id}/generate-xml',
    curl: `curl -X POST "${API_BASE}/shipments/<SHIPMENT_ID>/generate-xml" \\
  -H "Authorization: Bearer <TOKEN>" \\
  -o ICS2_ENS.xml`,
  },
  'link-report': {
    title: 'Link a PCF report to a shipment',
    method: 'POST',
    path: '/api/shipments/{id}/link-report',
    curl: `curl -X POST "${API_BASE}/shipments/<SHIPMENT_ID>/link-report" \\
  -H "Authorization: Bearer <TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"report_id":"<PCF_REPORT_ID>"}'`,
  },
  'tuv-docs': {
    title: 'Preview TÜV Methodology (EN)',
    method: 'GET',
    path: '/api/tuv-docs/methodology-en',
    curl: `curl -X GET "${API_BASE}/tuv-docs/methodology-en" \\
  -H "Authorization: Bearer <YOUR_JWT_TOKEN>"`,
  },
  'tuv-zip': {
    title: 'Download TÜV documentation ZIP (admin only)',
    method: 'GET',
    path: '/api/admin/download-tuv-docs',
    curl: `curl -X GET "${API_BASE}/admin/download-tuv-docs" \\
  -H "Authorization: Bearer <ADMIN_JWT_TOKEN>" \\
  -o Veridex_TUV_Pack.zip`,
  },
  'api-keys': {
    title: 'Create an API key (Starter tier or higher)',
    method: 'POST',
    path: '/api/api-keys',
    curl: `curl -X POST "${API_BASE}/api-keys" \\
  -H "Authorization: Bearer <TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"My server"}'`,
  },
};

function CurlBlock({ sample }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(sample.curl);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className="font-mono text-[10px]">{sample.method}</Badge>
          <span className="font-mono text-xs text-muted-foreground truncate">{sample.path}</span>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={copy}
          className="h-7 text-xs shrink-0"
          data-testid={`copy-btn-${sample.path}`}
        >
          {copied
            ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-500" /> Copied</>
            : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
        </Button>
      </div>
      <pre className="bg-muted/50 border border-border rounded-md p-3 text-xs overflow-auto font-mono whitespace-pre-wrap leading-relaxed">
{sample.curl}
      </pre>
    </div>
  );
}

export default function Developers() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    setSending(true);
    try {
      await leadsAPI.create({ ...form, source: 'developers_page' });
      setSent(true);
      setForm({ name: '', email: '', company: '', message: '' });
      toast.success('Thanks — we will reach out within 24h');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-8" data-testid="developers-page">
      <div className="max-w-4xl">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Code2 className="w-7 h-7 text-primary" /> Developers
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Veridex One ships a RESTful JSON API. Generate an API key from
          Settings → API Keys (Starter tier or above), then hit any of the endpoints
          below. Full interactive docs live at <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`} target="_blank" rel="noreferrer" className="text-primary underline">Swagger UI (/api/docs)</a>.
        </p>
        <div className="mt-4 p-3 rounded-md bg-muted/40 border border-border text-xs text-muted-foreground" data-testid="rate-limit-note">
          <span className="font-semibold text-foreground">Rate limit:</span> each API key is capped at{' '}
          <span className="font-mono font-semibold text-foreground">100 requests per hour</span> (sliding window).
          When exceeded, the API returns <code className="px-1 py-0.5 bg-background rounded">429 Too Many Requests</code> with{' '}
          <code className="px-1 py-0.5 bg-background rounded">Retry-After</code>, <code className="px-1 py-0.5 bg-background rounded">X-RateLimit-Limit</code> and{' '}
          <code className="px-1 py-0.5 bg-background rounded">X-RateLimit-Remaining</code> headers.
          Keys also carry scopes (<span className="font-mono">read / write / admin</span>) — endpoints enforce the required scope.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="card-integrations-status">
          <div className="flex items-center gap-2 mb-4">
            <Plug className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">Integrations status</h2>
          </div>
          <ul className="space-y-2 text-sm" data-testid="integrations-list">
            {[
              { key: 'sentry', label: 'Sentry (error monitoring)', env: 'REACT_APP_ENABLE_SENTRY + REACT_APP_SENTRY_DSN' },
              { key: 'ga4', label: 'Google Analytics 4', env: 'REACT_APP_ENABLE_ANALYTICS + REACT_APP_GA_MEASUREMENT_ID' },
              { key: 'hubspot', label: 'HubSpot CRM tracking', env: 'REACT_APP_ENABLE_HUBSPOT + REACT_APP_HUBSPOT_PORTAL_ID' },
              { key: 'tawkto', label: 'Tawk.to live chat', env: 'REACT_APP_ENABLE_TAWKTO + REACT_APP_TAWKTO_PROPERTY_ID' },
              { key: 'googleForms', label: 'Google Forms (/feedback)', env: 'REACT_APP_FEEDBACK_FORM_URL' },
            ].map((row) => (
              <li key={row.key} className="flex items-start justify-between gap-3 border-b border-border pb-2 last:border-0" data-testid={`integration-row-${row.key}`}>
                <div>
                  <p className="font-medium">{row.label}</p>
                  <p className="text-[11px] text-muted-foreground font-mono">{row.env}</p>
                </div>
                {INTEGRATIONS[row.key]
                  ? <Badge className="bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15" data-testid={`integration-${row.key}-live`}>LIVE</Badge>
                  : <Badge variant="outline" className="text-muted-foreground" data-testid={`integration-${row.key}-off`}>Not configured</Badge>}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-muted-foreground mt-3">
            Each integration activates the moment its env var is filled — no redeploy, no code change.
          </p>
        </Card>

        <Card className="p-5" data-testid="card-samples">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">cURL quick-start</h2>
          </div>
          <Tabs defaultValue="pcf-reports" data-testid="samples-tabs">
            <TabsList className="grid grid-cols-3 mb-3 h-auto flex-wrap">
              <TabsTrigger value="pcf-reports" className="text-xs">PCF</TabsTrigger>
              <TabsTrigger value="shipments" className="text-xs">Shipments</TabsTrigger>
              <TabsTrigger value="ics2-xml" className="text-xs">ICS2 XML</TabsTrigger>
              <TabsTrigger value="link-report" className="text-xs">Link PCF</TabsTrigger>
              <TabsTrigger value="tuv-docs" className="text-xs">TÜV Preview</TabsTrigger>
              <TabsTrigger value="tuv-zip" className="text-xs">TÜV ZIP</TabsTrigger>
              <TabsTrigger value="api-keys" className="text-xs">API Key</TabsTrigger>
            </TabsList>
            {Object.entries(SAMPLES).map(([key, s]) => (
              <TabsContent key={key} value={key}>
                <h3 className="text-sm font-semibold mb-2">{s.title}</h3>
                <CurlBlock sample={s} />
              </TabsContent>
            ))}
          </Tabs>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs text-primary mt-4 hover:underline"
            data-testid="swagger-link"
          >
            Open full Swagger UI <ExternalLink className="w-3 h-3" />
          </a>
        </Card>

        <Card className="p-5" data-testid="card-book-demo">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">Book a demo</h2>
          </div>
          {sent ? (
            <div className="text-center py-10" data-testid="demo-sent">
              <Check className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm font-medium">Thanks — we will reach out within 24 hours.</p>
              <Button variant="outline" className="mt-4" onClick={() => setSent(false)} data-testid="send-another-btn">
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="demo-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Full name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="demo-name" />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="demo-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="demo-company" />
              </div>
              <div>
                <Label className="text-xs">Use case</Label>
                <Textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What CBAM / PCF workflow are you trying to automate?"
                  data-testid="demo-message"
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full" data-testid="demo-submit">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Request demo
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
